<template>
  <nav class="iso-nav">
    <div class="iso-nav-inner iso-container">
      <router-link to="/" class="iso-nav-brand">
        <span class="brand-icon">◆</span>
        <span class="brand-text">{{ $t('site.name') }}</span>
      </router-link>

      <div class="iso-nav-links" :class="{ open: menuOpen }">
        <router-link
          v-for="item in navItems"
          :key="item.path"
          :to="item.path"
          class="iso-nav-link"
          :class="{ active: isActive(item.path) }"
          @click="menuOpen = false"
        >
          {{ item.label }}
        </router-link>
      </div>

      <div class="iso-nav-actions">
        <slot name="actions" />
        <button class="iso-hamburger" @click="menuOpen = !menuOpen" :aria-label="$t('nav.menu')">
          <span :class="{ rotated: menuOpen }"></span>
          <span :class="{ rotated: menuOpen }"></span>
          <span :class="{ rotated: menuOpen }"></span>
        </button>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
const route = useRoute()
const menuOpen = ref(false)

const navItems = computed(() => [
  { path: '/', label: t('nav.home') },
  { path: '/category/gaokao', label: t('nav.category') },
  { path: '/search', label: t('nav.search') },
  { path: '/feedback', label: t('nav.feedback') },
])

function isActive(path: string): boolean {
  if (path === '/') return route.path === '/'
  return route.path.startsWith(path)
}
</script>

<style scoped>
.iso-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: var(--header-height);
  background: rgba(10, 14, 26, 0.85);
  backdrop-filter: blur(16px);
  border-bottom: 1px solid var(--iso-border);
  box-shadow: 0 2px 24px rgba(0, 0, 0, 0.3), 0 0 1px var(--iso-cyan-glow);
  transform: perspective(1200px) rotateX(0deg);
}

.iso-nav-inner {
  display: flex;
  align-items: center;
  height: 100%;
  gap: 32px;
}

.iso-nav-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  text-decoration: none;
  flex-shrink: 0;
}

.brand-icon {
  color: var(--iso-cyan);
  font-size: 22px;
  text-shadow: 0 0 12px var(--iso-cyan-glow);
  animation: iso-glow-pulse 3s ease-in-out infinite;
}

.brand-text {
  background: linear-gradient(135deg, #fff 0%, var(--iso-cyan) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.iso-nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
}

.iso-nav-link {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--iso-text-dim);
  border-radius: 8px;
  transition: all 0.25s;
  text-decoration: none;
  position: relative;
}

.iso-nav-link:hover {
  color: var(--iso-cyan);
  background: rgba(0, 212, 255, 0.06);
}

.iso-nav-link.active {
  color: var(--iso-cyan);
  background: rgba(0, 212, 255, 0.1);
  box-shadow: 0 0 12px rgba(0, 212, 255, 0.1);
}

.iso-nav-link.active::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 2px;
  background: var(--iso-cyan);
  border-radius: 1px;
  box-shadow: 0 0 6px var(--iso-cyan-glow);
}

.iso-nav-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}

.iso-hamburger {
  display: none;
  flex-direction: column;
  gap: 5px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
}

.iso-hamburger span {
  display: block;
  width: 22px;
  height: 2px;
  background: var(--iso-text);
  border-radius: 1px;
  transition: all 0.3s;
}

.iso-hamburger span.rotated:nth-child(1) {
  transform: rotate(45deg) translate(5px, 5px);
}

.iso-hamburger span.rotated:nth-child(2) {
  opacity: 0;
}

.iso-hamburger span.rotated:nth-child(3) {
  transform: rotate(-45deg) translate(5px, -5px);
}

@media (max-width: 768px) {
  .iso-nav-links {
    position: fixed;
    top: var(--header-height);
    left: 0;
    right: 0;
    background: rgba(10, 14, 26, 0.95);
    backdrop-filter: blur(16px);
    flex-direction: column;
    padding: 16px;
    gap: 4px;
    border-bottom: 1px solid var(--iso-border);
    transform: translateY(-100%);
    opacity: 0;
    pointer-events: none;
    transition: all 0.3s ease;
  }

  .iso-nav-links.open {
    transform: translateY(0);
    opacity: 1;
    pointer-events: auto;
  }

  .iso-hamburger {
    display: flex;
  }
}
</style>
